import { useEffect, useState, useCallback } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/**
 * Captures the browser's PWA install offer.
 *
 * `beforeinstallprompt` is the only signal the platform gives an embedded app
 * that the user actually qualifies (HTTPS, manifest, service worker all in
 * place). Without this, "install app" only ever shows in the address bar —
 * invisible to most karigars and counter staff.
 *
 * THE CATCH: this event fires AT MOST ONCE per browser profile, ever. Chrome
 * decides on its own schedule — usually a few seconds after the first visit —
 * and once it has fired, it never fires again for that origin. That produces
 * two failure modes this hook is built to survive:
 *
 *   1. The event fires before React mounts (the bundle is still loading on a
 *      cold visit). The listener is attached too late, the offer is lost, and
 *      the button never appears. Nothing retries it, because nothing can.
 *   2. The event is captured, `prompt()` is called, the user dismisses the
 *      dialog. The event object is now spent. Calling `prompt()` again is a
 *      silent no-op — no dialog, no error, no feedback. A button that appears
 *      and does nothing.
 *
 * The mitigation for both is to capture the event as early as it is physically
 * possible to do so — before the app bundle runs — and to keep it. The
 * `gatiq-install-offer` module below installs a listener during HTML parsing,
 * stashes the event on `window`, and this hook picks it up on mount. A listener
 * registered at parse time cannot miss an event that has not fired yet.
 *
 * Returns:
 *   • `available` — an install offer is in hand and has not been used
 *   • `prompt()` — show the install dialog and resolve with the outcome
 *   • `installed` — true once the user accepts (we then hide the button)
 */
export function useInstallPrompt(): {
  available: boolean;
  installed: boolean;
  prompt: () => Promise<"accepted" | "dismissed" | "unavailable">;
} {
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBefore = (e: Event): void => {
      // Stop Chrome from showing its own automatic mini-infobar; ours is the
      // only prompt the user should see.
      e.preventDefault();
      setEvt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = (): void => setInstalled(true);

    // Already installed before this hook mounted (the app was installed on an
    // earlier visit, and the early script in index.html saw it).
    if ((window as { __gatiqAppInstalled?: boolean }).__gatiqAppInstalled) {
      setInstalled(true);
    }

    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);

    // An offer that arrived before this listener existed. The early capture in
    // `index.html` holds it; take it now.
    const early = (window as { __gatiqInstallOffer?: Event }).__gatiqInstallOffer;
    if (early) {
      (window as { __gatiqInstallOffer?: Event }).__gatiqInstallOffer = undefined;
      onBefore(early);
    }

    // Some browsers (iOS Safari) do not fire this at all — no harm in trying.
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const prompt = useCallback(async (): Promise<"accepted" | "dismissed" | "unavailable"> => {
    if (!evt) return "unavailable";
    await evt.prompt();
    const choice = await evt.userChoice;
    if (choice.outcome === "accepted") setInstalled(true);
    /*
     * The event object is spent after one prompt() — the browser will not
     * re-offer through the same object. But dismissing is not the same as
     * refusing forever: Chrome re-evaluates installability and fires a fresh
     * event on a later visit. So the button is allowed to come back, rather
     * than being permanently hidden on one dismissal.
     */
    setEvt(null);
    return choice.outcome;
  }, [evt]);

  return { available: Boolean(evt), installed, prompt };
}
